import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import Header from "../components/Form/Header";
import MainContent from "../components/Form/MainContent";
import FooterCart from "../components/Form/FooterCart";
import { formatCurrency } from "../utils/numberUtils";
import './ListagemProdutos.css';

const ListagemProdutos = () => {
  const { fetchData } = useApi();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const groupRefs = useRef({});
  const groupBarRef = useRef(null);
  const isScrolling = useRef(false);

  // Busca grupos e itens de cada grupo ao montar o componente
  useEffect(() => {
    const fetchGroupsAndItems = async () => {
      try {
        // Busca grupos
        const groupsResult = await fetchData('/GrupoDelivery');
        setGroups(groupsResult);

        // Busca itens para cada grupo usando for...of
        const allItems = [];
        for (const group of groupsResult) {
          const itemsResult = await fetchData('/ProdutoDelivery/ProdutosByGrupo', 
            { grupoId: group.id }
          );
          // Adiciona os itens com referência ao grupo
          allItems.push(...itemsResult.map(item => ({
            ...item,
            grupo: { id: group.id, descricao: group.descricao }
          })));
        }
        setItems(allItems);

        // Define o grupo inicial selecionado
        if (groupsResult.length > 0) {
          setSelectedGroupId(groupsResult[0].id);
        }
        setLoading(false);
      } catch (error) {
        setError("Erro ao buscar dados: " + error);
        setLoading(false);
      }
    };

    fetchGroupsAndItems();
  }, []);
  
  // Manipula a seleção de grupo
  const handleGroupClick = (groupId) => {
    setSelectedGroupId(groupId);
    const groupElement = groupRefs.current[groupId];
    if (groupElement) {
      isScrolling.current = true;
      groupElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Centraliza o grupo selecionado na barra de grupos
      const groupButton = groupBarRef.current.querySelector(`[data-group-id="${groupId}"]`);
      if (groupButton) {
        groupButton.scrollIntoView({ behavior: 'smooth', inline: 'center' });
      }
      setTimeout(() => {
        isScrolling.current = false;
      }, 1000);
    }
  };

  // Manipula a rolagem para atualizar o grupo selecionado
  useEffect(() => {
    const handleScroll = () => {
      if (isScrolling.current) return;

      let currentGroupId = selectedGroupId;
      let minDistance = Infinity;

      groups.forEach((group) => {
        const element = groupRefs.current[group.id];
        if (element) {
          const rect = element.getBoundingClientRect();
          const distance = Math.abs(rect.top);
          if (distance < minDistance) {
            minDistance = distance;
            currentGroupId = group.id;
          }
        }
      });

      if (currentGroupId !== selectedGroupId) {
        setSelectedGroupId(currentGroupId);
        // Centraliza o grupo selecionado na barra de grupos
        const groupButton = groupBarRef.current.querySelector(`[data-group-id="${currentGroupId}"]`);
        if (groupButton) {
          groupButton.scrollIntoView({ behavior: 'smooth', inline: 'center' });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [groups, selectedGroupId]);

  // Manipula o clique no item para navegar para a página de detalhes
  const handleItemClick = (item) => {
    navigate(`/itemdetails/${item.id}`, { state: { itemSel: item } });
  };

  if (loading) return <p>Carregando...</p>;
  if (error) return <p>Erro: {error}</p>;

  return (
    <>
      <Header urlBack="/ordermenu" titulo="Produtos" />
      
      {/* Barra de grupos fixa no topo */}
      <div className="group-bar" ref={groupBarRef}>
        {groups.map((group) => (
          <button
            key={group.id}
            data-group-id={group.id}
            className={`group-button ${selectedGroupId === group.id ? 'selected' : ''}`}
            onClick={() => handleGroupClick(group.id)}
          >
            {group.descricao}
          </button>
        ))}
      </div>

      <MainContent>
        {groups.map((group) => (
          <div key={group.id} ref={(el) => (groupRefs.current[group.id] = el)} className="group-section">
            <h2 className="group-title">{group.descricao}</h2>
            {items
              .filter((item) => item.grupo?.id === group.id)
              .map((item) => (
                <div key={item.id} className="product-item" onClick={() => handleItemClick(item)}>
                  <img
                    src={
                      item.imagem?.arquivo
                        ? `data:image/jpeg;base64,${item.imagem.arquivo}`
                        : '/images/image_default.jpg'
                    }
                    alt={item.descricao}
                    className="product-img"
                  />
                  <div className="product-info">
                    <h3 className="product-name">{item.descricao}</h3>
                    <p className="product-details">{item.ingredientes}</p>
                    <p className="product-price">{formatCurrency(item.preco)}</p>
                  </div>
                </div>
              ))}
          </div>
        ))}
      </MainContent>

      <FooterCart />
    </>
  );
};

export default ListagemProdutos;
